import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Label, SearchField, Spinner, TruncatedText } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { QueryLoadFailure } from "@/lib/queryLoadFailure";
import { QueryLoadError } from "./QueryLoadError";

export type PickerPanelItem = {
  id: string;
  title: string;
  subtitle?: string;
  detail?: string;
  trailing?: string;
  trailingDetail?: string;
  selected: boolean;
  onSelect: () => void;
};

export interface PickerPanelProps {
  stepLabel?: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchDisabled?: boolean;
  searchAccessory?: ReactNode;
  isLoading: boolean;
  isRefreshing?: boolean;
  /** Set when the item query failed: shown instead of the (empty) list. */
  loadError?: QueryLoadFailure;
  emptyMessage: string;
  items: PickerPanelItem[];
  className?: string;
  listClassName?: string;
  listVariant?: "stack" | "divided";
}

export function PickerPanel({
  stepLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  searchDisabled = false,
  searchAccessory,
  isLoading,
  isRefreshing = false,
  loadError,
  emptyMessage,
  items,
  className,
  listClassName,
  listVariant = "stack",
}: PickerPanelProps) {
  const { t } = useTranslation("common");
  const divided = listVariant === "divided";

  return (
    <div className={className}>
      {stepLabel ? (
        <Label as="p" spacing="relaxed">{stepLabel}</Label>
      ) : null}
      <SearchField
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        searchLabel={t("search")}
        clearSearchLabel={t("clearSearch")}
        disabled={searchDisabled}
        className="mb-2 max-w-none"
      />
      {searchAccessory ? <div className="mb-2">{searchAccessory}</div> : null}
      <div
        className={cn(
          divided
            ? "max-h-48 overflow-y-auto rounded-xl border border-surface-200"
            : "max-h-56 space-y-1 overflow-y-auto rounded-lg border border-surface-200 p-2",
          listClassName,
        )}
      >
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner loadingLabel={t("loading")} />
          </div>
        ) : loadError ? (
          <QueryLoadError failure={loadError} />
        ) : (
          <>
            {items.length === 0 ? (
              <p
                className={cn(
                  "text-sm text-surface-500",
                  divided ? "px-4 py-8 text-center" : "px-3 py-2",
                )}
              >
                {emptyMessage}
              </p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={item.onSelect}
                  className={cn(
                    "text-left text-sm",
                    divided
                      ? cn(
                          "flex w-full items-start justify-between gap-3 border-b border-surface-100 px-4 py-3 last:border-b-0",
                          item.selected
                            ? "bg-primary-50"
                            : "hover:bg-surface-50",
                        )
                      : cn(
                          "w-full rounded-md px-3 py-2",
                          item.selected
                            ? "bg-primary-100 text-primary-800"
                            : "text-surface-700 hover:bg-surface-50",
                        ),
                  )}
                >
                  <div className={item.trailing ? "min-w-0" : undefined}>
                    {divided ? (
                      <TruncatedText className="font-medium text-surface-900">
                        {item.title}
                      </TruncatedText>
                    ) : (
                      <div>{item.title}</div>
                    )}
                    {item.subtitle ? (
                      divided ? (
                        <TruncatedText
                          as="div"
                          className="mt-0.5 text-xs text-surface-500"
                        >
                          {item.subtitle}
                        </TruncatedText>
                      ) : (
                        <div className="text-xs text-surface-500">
                          {item.subtitle}
                        </div>
                      )
                    ) : null}
                    {item.detail ? (
                      <div className="mt-1 text-xs text-surface-500">
                        {item.detail}
                      </div>
                    ) : null}
                  </div>
                  {item.trailing ? (
                    <div className="shrink-0 text-right text-xs text-surface-500">
                      <div>{item.trailing}</div>
                      {item.trailingDetail ? (
                        <div>{item.trailingDetail}</div>
                      ) : null}
                    </div>
                  ) : null}
                </button>
              ))
            )}
            {isRefreshing ? (
              <div className="flex justify-center py-3">
                <Spinner size="sm" loadingLabel={t("loading")} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
